import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Hammer from 'hammerjs';

import { track } from '@xanadu/ui-shared/interfaces/woopra';

import translatedConstants from '../../constants/customerOnboarding';

import Card from './Card';

import styles from './CustomerOnboardingCards.module.scss';
import cardStyles from './Card/Card.module.scss';

const CustomerOnboardingCards = ({
  isFuturesOrPredictions,
  isLoggedIn,
  toggleCustomerOnboardingPopup,
  registrationRequest
}) => {
  const {
    PREDICTIONS_STEPS_CONTENT = [],
    STEPS_CONTENT = [],
  } = translatedConstants;
  const stepsContent = isFuturesOrPredictions ? PREDICTIONS_STEPS_CONTENT : STEPS_CONTENT;
  const [activeIndex, setActiveIndex] = useState(0);
  const cardsRef = useRef(null);
  const refs = useMemo(
    () => stepsContent.map(() => createRef()),
    [stepsContent.length]
  );

  const computeCards = ({ next, prev } = {}) => {
    const newCards = refs.filter(ref => (
      !ref.current.classList.contains('removed')
    ));

    if (!next && !prev) {
      newCards.forEach((cardRef, index) => {
        // eslint-disable-next-line no-param-reassign
        cardRef.current.style.zIndex = stepsContent.length - index;
      });
    }

    if (next) {
      setActiveIndex(currentIndex => currentIndex + 1);
    }

    if (prev) {
      setActiveIndex(currentIndex => currentIndex - 1);
    }
  };

  const onEndTour = useCallback(() => {
    if (activeIndex < stepsContent.length) {
      track('Marketing', 'Tour closed');
    }

    toggleCustomerOnboardingPopup({
      visible: false,
      firstTimeVisitor: false
    });
  }, []);

  const onJoinNow = useCallback(() => {
    const timer = setTimeout(() => {
      toggleCustomerOnboardingPopup({
        visible: false,
        firstTimeVisitor: false
      });
      registrationRequest({ show: true, promoRegistrationImage: null, checked: false });
      track('Marketing', 'Reg opened from Tour');
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const onPrevious = (element) => {
    element.classList.remove('removed');

    // eslint-disable-next-line no-param-reassign
    element.style.transform = 'translateX(0)';

    computeCards({
      prev: activeIndex
    });
  };

  const onNext = (element) => {
    const moveOutWidth = document.body.clientWidth;

    // eslint-disable-next-line no-param-reassign
    element.style.transform = `translateX(-${moveOutWidth}px)`;

    computeCards({
      next: true
    });
  };

  useEffect(() => {
    const { current: domElement } = cardsRef;
    let hammer;

    if (domElement) {
      hammer = new Hammer.Manager(domElement, {
        recognizers: [
          [Hammer.Pan],
        ]
      });

      hammer.on('pan', (event) => {
        if (event.deltaX === 0) return;
        if (event.center.x === 0 && event.center.y === 0) return;

        if (event.deltaX > 0 && activeIndex) {
          // eslint-disable-next-line no-param-reassign
          refs[activeIndex - 1].current.zIndex += 1;

          const moveOutWidth = document.body.clientWidth;

          refs[activeIndex - 1].current.classList.add(cardStyles['main--moving']);
          refs[activeIndex - 1].current.style.transform = `translateX(${-moveOutWidth + Math.abs(event.deltaX)}px)`;
        } else {
          refs[activeIndex].current.classList.add(cardStyles['main--moving']);
          // eslint-disable-next-line no-param-reassign
          refs[activeIndex].current.style.transform = `translateX(${event.deltaX}px)`;
        }
      });

      hammer.on('panend', (event) => {
        const swipeBack = event.deltaX > 0;

        const keep = (!swipeBack && event.deltaX > 90)
          || (swipeBack && event.deltaX < -90)
          || Math.abs(event.velocityX) < 0.5;

        const viewedCardsIndex = activeIndex + (swipeBack && activeIndex ? -1 : 0);

        refs[viewedCardsIndex].current.classList.toggle('removed', !keep);

        refs[viewedCardsIndex].current.classList.remove(cardStyles['main--moving']);

        if (swipeBack) {
          onPrevious(refs[viewedCardsIndex].current);

          return;
        }

        if (keep) {
          // eslint-disable-next-line no-param-reassign
          refs[viewedCardsIndex].current.style.transform = '';
        } else {
          onNext(refs[viewedCardsIndex].current);
        }
      });
    }

    return () => {
      if (hammer) {
        hammer.destroy();
      }
    };
  }, [activeIndex]);

  useEffect(() => {
    if (activeIndex) {
      track('Marketing', `Tour Step ${activeIndex} Viewed`);
    }

    if (activeIndex === stepsContent.length) {
      onEndTour();
    }
  }, [activeIndex, stepsContent.length]);

  useEffect(() => {
    computeCards();
  }, []);

  const mainClass = classNames({
    [styles.main]: true,
  });

  return (
    <div className={mainClass} ref={cardsRef}>
      {stepsContent.map(({ title, content }, index) => (
        <Card
          key={title}
          index={index}
          title={title}
          content={content}
          ref={refs[index]}
          isLoggedIn={isLoggedIn}
          activeIndex={activeIndex}
          isVisible={activeIndex === index}
          stepsContent={stepsContent}
          onEndTour={onEndTour}
          onJoinNow={onJoinNow}
          onPrevious={() => {
            onPrevious(refs[activeIndex - 1].current);
          }}
          onNext={() => {
            onNext(refs[activeIndex].current);
          }}
        />
      ))}
    </div>
  );
};

CustomerOnboardingCards.propTypes = {
  isFuturesOrPredictions: PropTypes.bool,
  toggleCustomerOnboardingPopup: PropTypes.func.isRequired,
  registrationRequest: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
};

CustomerOnboardingCards.defaultProps = {
  isFuturesOrPredictions: false,
};

export default CustomerOnboardingCards;
